import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Check, CheckCheck, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [askingPerm, setAskingPerm] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user/notifications`);
      const notifs = res.data || [];
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.is_read).length);
    } catch {}
  }, []);

  const subscribeToPush = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const reg = await navigator.serviceWorker.register('/sw-push.js');
      await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON() });
      setPushEnabled(true);
    } catch (err) {
      console.error('Push subscribe error:', err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.getRegistration('/sw-push.js').then(reg => {
        if (reg) reg.pushManager.getSubscription().then(sub => setPushEnabled(!!sub));
      });
      if (Notification.permission === 'granted') subscribeToPush();
    }
    return () => clearInterval(interval);
  }, [fetchNotifications, subscribeToPush]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleEnablePush = async () => {
    setAskingPerm(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm === 'granted') await subscribeToPush();
    } catch {}
    setAskingPerm(false);
  };

  const markAsRead = async (id) => {
    try {
      await axios.post(`${API}/user/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/user/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const typeColor = { info: 'bg-blue-500', warning: 'bg-amber-500', success: 'bg-emerald-500', error: 'bg-red-500' };

  return (
    <div ref={ref} className="relative" data-testid="notification-bell">
      <button onClick={() => setOpen(!open)} className="relative p-2 rounded-xl hover:bg-gray-100 transition-colors" data-testid="bell-button">
        <Bell size={20} className="text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="unread-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[380px] bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/80">
            <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-[11px] text-[#35AFE4] font-semibold hover:underline flex items-center gap-1" data-testid="mark-all-read">
                  <CheckCheck size={12} /> Tout lire
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-gray-200 rounded-lg"><X size={14} className="text-gray-400" /></button>
            </div>
          </div>

          {!pushEnabled && typeof Notification !== 'undefined' && Notification.permission !== 'denied' && (
            <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
              <p className="text-xs text-blue-800 mb-2">Activez les notifications push pour ne rien manquer</p>
              <button onClick={handleEnablePush} disabled={askingPerm}
                className="text-xs font-bold text-white bg-[#35AFE4] px-3 py-1.5 rounded-lg hover:bg-[#2d7fa5] transition-colors disabled:opacity-50 flex items-center gap-1"
                data-testid="enable-push-btn">
                {askingPerm ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
                Activer les notifications
              </button>
            </div>
          )}

          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell size={28} className="mx-auto text-gray-300 mb-2" />
                <p className="text-xs text-gray-400">Aucune notification</p>
              </div>
            ) : notifications.map((n) => (
              <div key={n.id}
                className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 hover:bg-gray-50/50 transition-colors cursor-pointer ${!n.is_read ? 'bg-blue-50/30' : ''}`}
                onClick={() => { if (!n.is_read) markAsRead(n.id); if (n.link) { setOpen(false); navigate(n.link); } }}
                data-testid={`notification-item-${n.id}`}>
                <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${!n.is_read ? (typeColor[n.notification_type] || 'bg-blue-500') : 'bg-transparent'}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${!n.is_read ? 'font-bold text-gray-900' : 'font-medium text-gray-600'}`}>{n.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message?.replace(/<[^>]+>/g, '').slice(0, 100)}</p>
                  <p className="text-[10px] text-gray-400 mt-1">
                    {n.sent_date ? new Date(n.sent_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                  </p>
                </div>
                {!n.is_read && (
                  <button onClick={(e) => { e.stopPropagation(); markAsRead(n.id); }} className="p-1 hover:bg-blue-100 rounded-lg flex-shrink-0">
                    <Check size={14} className="text-[#35AFE4]" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
